import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Header from '../components/Header';
import HeaderTitle from '../components/HeaderTitle';
import { NavbarToggle } from '../components/Navbar';
import { useSidebar } from '../layouts/Dashboard';
import { logout } from '../services/authService';
import {
  addTravelOption,
  deleteTravelOption,
  getTravelOptions,
  updateTravelOption,
} from '../services/travelOptionsService';
import './TravelOptions.css';

const ResourceSection = ({ title, type, options, onRefresh }) => {
  const [newName, setNewName] = useState('');
  const [editingName, setEditingName] = useState('');
  const [editingValue, setEditingValue] = useState('');

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [options]
  );

  const handleAdd = () => {
    try {
      addTravelOption(type, newName);
      setNewName('');
      onRefresh();
    } catch (error) {
      alert(error.message || `Failed to add ${title.toLowerCase()}`);
    }
  };

  const handleStartEdit = (name) => {
    setEditingName(name);
    setEditingValue(name);
  };

  const handleSaveEdit = () => {
    try {
      updateTravelOption(type, editingName, editingValue);
      setEditingName('');
      setEditingValue('');
      onRefresh();
    } catch (error) {
      alert(error.message || `Failed to update ${title.toLowerCase()}`);
    }
  };

  return (
    <div className="travel-options-card">
      <div className="travel-options-card-header">
        <h3>{title}</h3>
        <span>{sortedOptions.length} total</span>
      </div>

      <div className="travel-options-add-row">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={`Add new ${type === 'hotels' ? 'hotel' : 'flight'}...`}
          className="travel-options-input"
        />
        <button type="button" onClick={handleAdd} className="travel-options-primary-btn">
          Add
        </button>
      </div>

      <div className="travel-options-list">
        {sortedOptions.length === 0 ? (
          <p className="travel-options-empty">No items yet.</p>
        ) : (
          sortedOptions.map((name) => {
            const isEditing = editingName === name;
            return (
              <div key={name} className="travel-options-item">
                {isEditing ? (
                  <input
                    type="text"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    className="travel-options-input"
                  />
                ) : (
                  <span className="travel-options-item-name">{name}</span>
                )}

                <div className="travel-options-actions">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        className="travel-options-secondary-btn"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingName('');
                          setEditingValue('');
                        }}
                        className="travel-options-secondary-btn"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleStartEdit(name)}
                        className="travel-options-secondary-btn"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onRefresh({ type, title, name })}
                        className="travel-options-danger-btn"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const TravelOptions = () => {
  const history = useHistory();
  const { toggleSidebar } = useSidebar();
  const [options, setOptions] = useState(() => getTravelOptions());
  const [pendingDelete, setPendingDelete] = useState(null);

  const refreshOptions = (deleteRequest = null) => {
    if (deleteRequest) {
      setPendingDelete(deleteRequest);
      return;
    }
    setOptions(getTravelOptions());
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    try {
      deleteTravelOption(pendingDelete.type, pendingDelete.name);
      setPendingDelete(null);
      setOptions(getTravelOptions());
    } catch (error) {
      alert(error.message || `Failed to delete ${pendingDelete.title.toLowerCase()}`);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      history.push('/login');
    } catch (error) {
      console.error('Logout error:', error);
      history.push('/login');
    }
  };

  return (
    <div className="travel-options-page">
      <Header>
        <div className="packages-topbar">
          <div className="effektToggle">
            <NavbarToggle onToggle={toggleSidebar} />
            <HeaderTitle>Hotels & Flights</HeaderTitle>
          </div>
          <button className="logout-button" onClick={handleLogout} type="button">
            Logout
          </button>
        </div>
      </Header>

      <div className="travel-options-section">
        <div className="section-header">
          <h2>Hotels and Flights Management</h2>
          <p className="section-subtitle">
            Add, edit, and delete hotels or flights used in package forms.
          </p>
        </div>

        <div className="travel-options-grid">
          <ResourceSection
            title="Hotels"
            type="hotels"
            options={options.hotels || []}
            onRefresh={refreshOptions}
          />
          <ResourceSection
            title="Flights"
            type="flights"
            options={options.flights || []}
            onRefresh={refreshOptions}
          />
        </div>
      </div>

      {pendingDelete && (
        <div className="travel-options-modal-overlay" onClick={() => setPendingDelete(null)}>
          <div className="travel-options-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete {pendingDelete.title.slice(0, -1)}</h3>
            <p>
              Are you sure you want to delete <strong>{pendingDelete.name}</strong>?
            </p>
            <div className="travel-options-modal-actions">
              <button
                type="button"
                className="travel-options-secondary-btn"
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="travel-options-danger-btn"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelOptions;
